import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, ChevronRight, Check, RefreshCw, Table2, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────────

interface SheetTab {
  title: string;
  sheetId: number;
}

interface SyncConfig {
  spreadsheetId: string;
  spreadsheetName?: string;
  tabName: string;
  columnMapping: Record<string, string>;
  headerRow: number;
  dateColumn: string;
  lastSyncAt?: string | null;
}

interface SyncResult {
  updated: number;
  created: number;
  skipped: number;
  skipReasons?: {
    emptyDate?: number;
    unparseableDate?: number;
    noMatchingEntry?: number;
    noUpdates?: number;
  };
  skipSamples?: Array<{ rowIndex: number; rawDate: string; reason: string }>;
  mappedFieldCount?: number;
  regionConflicts: never[];
  requiresResolution: false;
  message?: string;
}

const CRM_FIELDS: { key: string; label: string; required?: boolean }[] = [
  { key: "date", label: "Date (row matching)", required: true },
  { key: "status", label: "Status" },
  { key: "region", label: "Region" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "optionB", label: "Option B" },
  { key: "notes", label: "Notes" },
  { key: "driveTime", label: "Drive Time" },
  { key: "leadPages", label: "Lead Pgs" },
];

// ─── Component ───────────────────────────────────────────────────────────────

interface SheetsSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SheetsSyncDialog({ open, onOpenChange }: SheetsSyncDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 state
  const [selectedSpreadsheetId, setSelectedSpreadsheetId] = useState("");
  const [selectedSpreadsheetName, setSelectedSpreadsheetName] = useState("");
  const [selectedTab, setSelectedTab] = useState("");
  const [headerRow, setHeaderRow] = useState(1);
  const [manualUrl, setManualUrl] = useState("");

  // Drive connection info — the connected Google account is used (via the
  // spreadsheets.readonly scope) to read the pasted private sheet.
  const { data: driveStatus } = useQuery<{ connected: boolean; email: string | null }>({
    queryKey: ["/api/drive/status"],
    enabled: open,
  });

  // Extract spreadsheet ID from a Google Sheets URL — or accept a bare ID.
  function extractSheetId(input: string): string | null {
    const trimmed = input.trim();
    const urlMatch = trimmed.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (urlMatch) return urlMatch[1];
    // Accept a raw spreadsheet ID pasted on its own
    if (/^[a-zA-Z0-9-_]{20,}$/.test(trimmed)) return trimmed;
    return null;
  }

  function handleManualUrlChange(url: string) {
    setManualUrl(url);
    const id = extractSheetId(url);
    if (id) {
      setSelectedSpreadsheetId(id);
      setSelectedSpreadsheetName("Google Sheet");
      setSelectedTab("");
    } else {
      setSelectedSpreadsheetId("");
    }
  }

  // Step 2 state — column mapping
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

  // Step 3 state
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  // ── Load saved config
  const { data: savedConfig } = useQuery<SyncConfig | null>({
    queryKey: ["/api/sheets-sync/config"],
    enabled: open,
  });

  // When config loads, pre-fill state
  useEffect(() => {
    if (savedConfig && open) {
      setSelectedSpreadsheetId(savedConfig.spreadsheetId || "");
      setSelectedSpreadsheetName(savedConfig.spreadsheetName || "");
      setSelectedTab(savedConfig.tabName || "");
      setHeaderRow(savedConfig.headerRow || 1);
      setColumnMapping(savedConfig.columnMapping || {});
    }
  }, [savedConfig, open]);

  // ── Tabs list (when spreadsheet selected)
  const { data: tabs = [], isLoading: tabsLoading, error: tabsError } = useQuery<SheetTab[]>({
    queryKey: ["/api/sheets-sync/tabs", selectedSpreadsheetId],
    queryFn: async () => {
      if (!selectedSpreadsheetId) return [];
      const res = await apiRequest("GET", `/api/sheets-sync/tabs?spreadsheetId=${selectedSpreadsheetId}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to load tabs");
      }
      return res.json();
    },
    enabled: !!selectedSpreadsheetId && open && step === 1,
    retry: 1,
  });

  // ── Column headers (when spreadsheet + tab + headerRow selected)
  const { data: headersData, isLoading: headersLoading } = useQuery<{ headers: string[]; headerRow: number }>({
    queryKey: ["/api/sheets-sync/headers", selectedSpreadsheetId, selectedTab, headerRow],
    queryFn: async () => {
      if (!selectedSpreadsheetId || !selectedTab) return { headers: [], headerRow: 1 };
      const res = await apiRequest("GET", `/api/sheets-sync/headers?spreadsheetId=${selectedSpreadsheetId}&tab=${encodeURIComponent(selectedTab)}&headerRow=${headerRow}`);
      return res.json();
    },
    enabled: !!selectedSpreadsheetId && !!selectedTab && open && step === 2,
  });

  const headers = headersData?.headers || [];

  // Auto-map sheet headers → CRM fields when headers load in step 2.
  // Normalises both sides (lowercase, strip spaces/punctuation) then
  // checks a list of aliases per field. Only fills MISSING mappings so
  // the user's manual selections are never overwritten.
  // NOTE: must live AFTER the `headersData` useQuery — referencing it in
  // the deps array during render before its declaration trips the TDZ
  // ("Cannot access 'headersData' before initialization") and crashes
  // the whole spreadsheet page.
  useEffect(() => {
    if (!headersData?.headers?.length) return;
    const norm = (s: string) => s.toLowerCase().replace(/[\s_\-().\/]/g, "");
    // aliases: CRM field key → normalised header variants to try
    const ALIASES: Record<string, string[]> = {
      date:      ["date"],
      status:    ["status"],
      region:    ["region"],
      city:      ["city"],
      state:     ["state", "st"],
      optionB:   ["optionb", "option_b", "optb"],
      notes:     ["notes", "note"],
      driveTime: ["drivetime", "drive_time", "drivetime"],
      leadPages: ["leadpages", "leadpgs", "leadpg", "leadpage"],
    };
    const normHeaders = headersData.headers.map(norm);
    setColumnMapping(prev => {
      const next = { ...prev };
      for (const { key } of CRM_FIELDS) {
        if (next[key]) continue; // don't overwrite existing selection
        const aliases = ALIASES[key] ?? [norm(key)];
        for (const alias of aliases) {
          const idx = normHeaders.indexOf(alias);
          if (idx >= 0) {
            next[key] = headersData.headers[idx];
            break;
          }
        }
      }
      return next;
    });
  }, [headersData]);

  // ── Connect Google Sheets mutation
  const connectGoogleSheetsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", `/api/drive/auth?returnUrl=${encodeURIComponent(window.location.href)}`);
      const data = await response.json();
      return data.authUrl as string;
    },
    onSuccess: (authUrl: string) => {
      window.location.href = authUrl;
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to initiate Google Sheets connection",
        variant: "destructive",
      });
    },
  });

  // ── Disconnect Google Sheets mutation — clears the workspace's own Drive
  // token so the dialog returns to the disconnected (connect) state.
  const disconnectGoogleSheetsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/drive/disconnect");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drive/status"] });
      toast({ title: "Google Sheets Disconnected", description: "Your Google account has been disconnected." });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to disconnect Google Sheets",
        variant: "destructive",
      });
    },
  });

  // ── Save config mutation
  const saveConfigMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/sheets-sync/config", {
        spreadsheetId: selectedSpreadsheetId,
        spreadsheetName: selectedSpreadsheetName,
        tabName: selectedTab,
        columnMapping,
        headerRow,
        dateColumn: columnMapping["date"] || "",
      });
      return res.json();
    },
  });

  // ── Run sync mutation
  const runSyncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/sheets-sync/run", {
        spreadsheetId: selectedSpreadsheetId,
        tabName: selectedTab,
        columnMapping,
        headerRow,
        dateColumn: columnMapping["date"] || "",
      });
      return res.json();
    },
    onSuccess: (data: SyncResult) => {
      setSyncResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/schedule-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sheets-sync/config"] });
      setStep(3);
      toast({ title: "Sync Complete", description: data.message || `${data.updated} rows updated.` });
    },
    onError: (err: any) => {
      toast({ title: "Sync Failed", description: err.message || "Failed to sync from Google Sheets.", variant: "destructive" });
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(1);
      setSyncResult(null);
    }, 300);
  };

  const handleGoToStep2 = async () => {
    if (!selectedSpreadsheetId || !selectedTab) return;
    await saveConfigMutation.mutateAsync();
    setStep(2);
  };

  const handleGoToStep3 = async () => {
    // Save config with current mapping, then move to review
    await saveConfigMutation.mutateAsync();
    setStep(3);
  };

  const handleRunSync = () => {
    runSyncMutation.mutate();
  };

  const dateColumn = columnMapping["date"];
  const canRunSync = !!dateColumn;

  // ── Step indicator
  const steps = ["Pick Sheet", "Map Columns", "Review & Sync"];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Progress steps */}
        <div className="flex items-center gap-2 mb-1">
          {steps.map((label, i) => {
            const s = i + 1;
            const done = step > s;
            const active = step === s;
            return (
              <div key={s} className="flex items-center gap-2">
                <div className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors",
                  active ? "bg-primary text-primary-foreground border-primary" :
                  done ? "bg-primary/20 text-primary border-primary/40" :
                  "bg-muted text-muted-foreground border-border"
                )}>
                  {done ? <Check className="w-3.5 h-3.5" /> : s}
                </div>
                <span className={cn("text-xs hidden sm:inline", active ? "text-foreground font-medium" : "text-muted-foreground")}>
                  {label}
                </span>
                {s < steps.length && <div className={cn("h-0.5 w-6", done ? "bg-primary/40" : "bg-border")} />}
              </div>
            );
          })}
        </div>

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Sync from Google Sheets
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Select the Google Sheet and tab that contains your master schedule data."}
            {step === 2 && "Map each sheet column to the corresponding field in the CRM schedule."}
            {(step === 3 && !syncResult) && "Review your sync settings before running. The CRM will be updated to match the sheet."}
            {(step === 3 && syncResult) && "Sync complete! Your master schedule has been updated."}
          </DialogDescription>
        </DialogHeader>

        {/* ── STEP 1: Pick spreadsheet & tab ── */}
        {step === 1 && (
          <div className="space-y-4 py-2">
            {/* Connected Google account info — used to read the private sheet */}
            {driveStatus?.connected ? (
              <div className="flex items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  {driveStatus.email ? (
                    <span className="truncate">
                      Reading sheets as: <span className="font-medium text-foreground">{driveStatus.email}</span>
                    </span>
                  ) : (
                    <span>Google account connected</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="button-disconnect-google-sheets"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive flex-shrink-0"
                  disabled={disconnectGoogleSheetsMutation.isPending}
                  onClick={() => disconnectGoogleSheetsMutation.mutate()}
                >
                  {disconnectGoogleSheetsMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : null}
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-2">
                  <span>
                    No Google account connected. To read a <strong>private</strong> sheet, connect Google below. Publicly shared sheets can be read without connecting.
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="button-connect-google-sheets"
                    className="self-start border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                    disabled={connectGoogleSheetsMutation.isPending}
                    onClick={() => connectGoogleSheetsMutation.mutate()}
                  >
                    {connectGoogleSheetsMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : null}
                    Connect Google Sheets
                  </Button>
                </div>
              </div>
            )}

            {/* Paste spreadsheet ID or URL */}
            <div className="space-y-2">
              <Label>Google Sheet ID or URL</Label>
              <Input
                placeholder="https://docs.google.com/spreadsheets/d/... or the sheet ID"
                value={manualUrl}
                onChange={e => handleManualUrlChange(e.target.value)}
                data-testid="input-manual-sheet-url"
              />
              {manualUrl && !extractSheetId(manualUrl) && (
                <p className="text-xs text-destructive">Couldn't find a spreadsheet ID. Paste the full Google Sheets URL or the sheet's ID.</p>
              )}
              {selectedSpreadsheetId && (
                <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                  <CheckCircle2 className="w-3 h-3" />
                  Spreadsheet ID found — select a tab below.
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Open your sheet in Google Sheets and copy the link from the address bar. The connected account (or a public share link) must have access.
              </p>
            </div>

            {/* Tab picker — shown once a spreadsheet ID is provided */}
            {selectedSpreadsheetId && (
              <div className="space-y-2 pt-2 border-t">
                <Label>Select tab (sheet)</Label>
                {tabsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading tabs...
                  </div>
                ) : tabsError && driveStatus?.connected ? (
                  <p className="text-sm text-muted-foreground">
                    Could not read sheet tabs — make sure the sheet is shared with your connected account.
                  </p>
                ) : tabs.length === 0 && !tabsError ? (
                  <p className="text-sm text-muted-foreground">No tabs found. Make sure Google Drive is connected and the sheet is shared with the connected account.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {tabs.map(tab => (
                      <button
                        key={tab.sheetId}
                        onClick={() => setSelectedTab(tab.title)}
                        className={cn(
                          "px-3 py-1.5 rounded-md border text-sm transition-all",
                          selectedTab === tab.title
                            ? "border-primary bg-primary/10 text-primary font-medium"
                            : "border-border bg-card hover:bg-muted"
                        )}
                        data-testid={`button-select-tab-${tab.title}`}
                      >
                        <Table2 className="w-3.5 h-3.5 inline mr-1.5 align-middle" />
                        {tab.title}
                      </button>
                    ))}
                  </div>
                )}

                {selectedTab && (
                  <div className="flex items-center gap-3 pt-1">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">Header row:</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={headerRow}
                      onChange={e => setHeaderRow(parseInt(e.target.value) || 1)}
                      className="w-20 h-7 text-sm"
                      data-testid="input-header-row"
                    />
                    <span className="text-xs text-muted-foreground">Row that contains column headers</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 2: Column mapping ── */}
        {step === 2 && (
          <div className="space-y-3 py-2">
            {headersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Reading column headers...</span>
              </div>
            ) : headers.length === 0 ? (
              <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 text-sm">
                No column headers found. Check that your header row setting is correct and the sheet has data.
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Match each CRM field to the corresponding column in <strong>{selectedSpreadsheetName}</strong> → <strong>{selectedTab}</strong>.
                  Leave blank to skip that field.
                </p>
                <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                  {CRM_FIELDS.map(({ key, label, required }) => (
                    <div key={key} className="grid grid-cols-2 items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <Label className="text-sm">{label}</Label>
                        {required && <Badge variant="outline" className="text-xs py-0 h-4">Required</Badge>}
                      </div>
                      <Select
                        value={columnMapping[key] || ""}
                        onValueChange={val => setColumnMapping(prev => ({ ...prev, [key]: val === "_none" ? "" : val }))}
                      >
                        <SelectTrigger className="text-sm" data-testid={`select-column-${key}`}>
                          <SelectValue placeholder="— Skip —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Skip —</SelectItem>
                          {headers.map((h, i) => (
                            <SelectItem key={i} value={h || `Column ${i + 1}`}>
                              {h || `Column ${i + 1}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                {!columnMapping["date"] && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    You must map a Date column — it's used to match rows to schedule entries.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── STEP 3: Review & results ── */}
        {step === 3 && (
          <div className="space-y-4 py-2">
            {!syncResult ? (
              <>
                <div className="p-4 rounded-lg bg-muted/60 space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{selectedSpreadsheetName}</p>
                      <p className="text-muted-foreground">Tab: {selectedTab} · Header row: {headerRow}</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Column Mapping</p>
                    {CRM_FIELDS.filter(f => columnMapping[f.key]).map(f => (
                      <div key={f.key} className="flex items-center gap-2 text-xs">
                        <Check className="w-3.5 h-3.5 text-primary" />
                        <span className="font-medium">{f.label}</span>
                        <span className="text-muted-foreground">←</span>
                        <span className="text-muted-foreground">{columnMapping[f.key]}</span>
                      </div>
                    ))}
                    {CRM_FIELDS.filter(f => !columnMapping[f.key] && f.key !== "date").map(f => (
                      <div key={f.key} className="flex items-center gap-2 text-xs text-muted-foreground/60">
                        <span className="w-3.5 h-3.5 inline-block" />
                        <span>{f.label}</span>
                        <span>— not synced</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 text-xs space-y-1">
                  <p className="font-semibold">⚠ This will modify CRM data</p>
                  <p>Sheet values will update existing CRM schedule entries. Rows with a valid date that don't already exist in the CRM will be <strong>created</strong> as new entries.</p>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-green-800 dark:text-green-200">Sync Complete</p>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                      {syncResult.created > 0 && <>{syncResult.created} row{syncResult.created !== 1 ? "s" : ""} created · </>}
                      {syncResult.updated} row{syncResult.updated !== 1 ? "s" : ""} updated · {syncResult.skipped} skipped
                    </p>
                    {syncResult.skipReasons && syncResult.skipped > 0 && (
                      <ul className="text-xs text-green-700 dark:text-green-300 mt-2 list-disc list-inside space-y-0.5">
                        {!!syncResult.skipReasons.emptyDate && <li>{syncResult.skipReasons.emptyDate} row(s) had an empty date cell</li>}
                        {!!syncResult.skipReasons.unparseableDate && <li>{syncResult.skipReasons.unparseableDate} row(s) had an unrecognized date format</li>}
                        {!!syncResult.skipReasons.noUpdates && <li>{syncResult.skipReasons.noUpdates} row(s) had no values in any mapped column</li>}
                      </ul>
                    )}
                    {syncResult.mappedFieldCount === 0 && (
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 font-medium">
                        ⚠ No fields were mapped — go back and pick at least one column on the Map Columns step.
                      </p>
                    )}
                    {syncResult.skipSamples && syncResult.skipSamples.length > 0 && (
                      <details className="mt-2 text-xs text-green-700 dark:text-green-300">
                        <summary className="cursor-pointer hover:underline">Show sample skipped rows ({syncResult.skipSamples.length})</summary>
                        <ul className="mt-1 ml-4 list-disc space-y-0.5 font-mono">
                          {syncResult.skipSamples.map((s, i) => (
                            <li key={i}>Row {s.rowIndex}: "{s.rawDate || "(empty)"}" — {s.reason}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
                {savedConfig?.lastSyncAt && (
                  <p className="text-xs text-muted-foreground">
                    Last synced: {new Date(savedConfig.lastSyncAt).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Footer ── */}
        <DialogFooter className="flex-row justify-between gap-2 pt-2 border-t mt-1">
          <Button variant="outline" onClick={() => {
            if (step === 1) handleClose();
            else setStep(prev => (prev - 1) as any);
          }}>
            {step === 1 ? "Cancel" : "Back"}
          </Button>

          <div className="flex gap-2">
            {step === 1 && (
              <Button
                onClick={handleGoToStep2}
                disabled={!selectedSpreadsheetId || !selectedTab || saveConfigMutation.isPending}
                data-testid="button-sheets-next-step1"
              >
                {saveConfigMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Map Columns <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}

            {step === 2 && (
              <Button
                onClick={handleGoToStep3}
                disabled={!canRunSync || saveConfigMutation.isPending}
                data-testid="button-sheets-next-step2"
              >
                {saveConfigMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Review <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}

            {step === 3 && !syncResult && (
              <Button
                onClick={handleRunSync}
                disabled={runSyncMutation.isPending}
                data-testid="button-run-sync"
              >
                {runSyncMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync Now
              </Button>
            )}

            {step === 3 && syncResult && (
              <Button onClick={handleClose} data-testid="button-sync-done">
                Done
              </Button>
            )}

          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
